import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Button } from "./Button";
import { useState, useEffect, useRef } from "react";
import { getAllMailTemplates } from "../../service/mailBodyTemplateService";
import { useDispatch, useSelector } from "react-redux";
import { GetAllMailBodyTemplateDto } from "../../dtos/mailTemplate";
import { CreateInvoiceDto, GetInvoiceDto, InvoiceStatusEnum } from "../../types/invoice.types";
import { getTenantById } from "../../service/tenant/TenantService";
import { Checkbox } from "primereact/checkbox";
import { getTenantPaymentSettingsByTenantId } from "../../service/tenantPaymentSettings/TenantPaymentSettingsService";
import { showToast } from "../../store/slices/toastSlice";
import { getAllTemplates } from "../../service/template/TemplateServices";
import { getEmailsByClientId } from "../../service/client/ClientServices";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import { useNavigate } from "react-router-dom";
import { getTenantDetailByTenantId } from "../../service/tenantDetails/TenantDetailsService";

interface Props {
  emailTo: string;
  setEmailTo: (val: string) => void;
  sendDialogVisible: boolean;
  setSendDialogVisible: (val: boolean) => void;
  handleSendInvoice: (propsEmailTo: string, propsEmailCc: string, template_id?: number) => void;
  emailMessage: string;
  setEmailMessage: (val: string) => void;
  emailCc: string;
  setEmailCc: (val: string) => void;
  emailSubject: string;
  setEmailSubject: (val: string) => void;
  withPaymentLink: boolean;
  setWithPaymentLink: (val: boolean) => void;
  invoiceData: CreateInvoiceDto | GetInvoiceDto;
  total?: number;
  setInvoiceData: (val: any) => void;
  action?:(e: React.FormEvent, action: InvoiceStatusEnum,skipNavigate:boolean)=>void;
}

export const SendInvoiceDialog: React.FC<Props> = ({
  emailTo,
  setEmailTo,
  sendDialogVisible,
  setSendDialogVisible,
  handleSendInvoice,
  emailMessage,
  setEmailMessage,
  emailCc,
  setEmailCc,
  emailSubject,
  setEmailSubject,
  withPaymentLink,
  setWithPaymentLink,
  invoiceData,
  setInvoiceData,
  action,
  total = 0, // Default to 0 if not provided
}) => {
  const [mailTemplates, setMailTemplates] = useState<
    GetAllMailBodyTemplateDto[]
  >([]);
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<GetAllMailBodyTemplateDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(true); // Toggle between preview and edit
  const dispatch = useDispatch();
  const [companyName, setCompanyName] = useState<string>("Default");
  const localization = useSelector((state: any) => state.localization);
  const [toEmailList, setToEmailList] = useState<any[]>([]);
  const [ccEmailList, setCcEmailList] = useState<any[]>([]);

  const [toEmails, setToEmails] = useState<any[]>([]);
  const [ccEmails, setCcEmails] = useState<any[]>([]);

  // New state for tracking input values
  const [toInputValue, setToInputValue] = useState("");
  const [ccInputValue, setCcInputValue] = useState("");

  const overlayRef = useRef<OverlayPanel>(null);
  const [showOverlayMessage, setShowOverlayMessage] = useState(false);

  const [isTenantInfoMissing, setIsTenantInfoMissing] = useState<boolean>(false);

useEffect(() => {
  const fetchTenantData = async () => {
    const tenantIdString = localStorage.getItem('tenantId');
    if(!tenantIdString) return 
    const tenantId = JSON.parse(tenantIdString)

    const res = await getTenantDetailByTenantId(dispatch, tenantId);
    const data = res;

    // Required fields to check
    const requiredFields = [
      'address_line1',
      'city',
      'state',
      'postal_code',
      'country_id',
      'logo',
    ];

    // Check if any required field is missing or null/empty
    const hasMissing = requiredFields.some((field) => !data?.[field]);
    setIsTenantInfoMissing(hasMissing);
  };

  fetchTenantData();
}, []);

  const showToastForPaymentSetting = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;
    const isSettingsExist = await getTenantPaymentSettingsByTenantId(
      dispatch,
      tenantId
    );

    if (!isSettingsExist) {
      setWithPaymentLink(false);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Invoice payment settings is not exist!",
          life: 3000,
        })
      );
    }
  };

  const handleSend = () => {
    const toEmailsString = toEmails.join(",");
    const ccEmailsString = ccEmails.join(",");
    handleSendInvoice(toEmailsString, ccEmailsString, invoiceData?.choosedTemplateId);
  };

  // Handle Enter key press for TO emails
  const handleToKeyDown = (e: any) => {
    if (e.key === "Enter" && toInputValue.trim()) {
      e.preventDefault();

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(toInputValue)) {
        dispatch(
          showToast({
            severity: "error",
            summary: "Invalid Email",
            detail: "Please enter a valid email address",
            life: 3000,
          })
        );
        return;
      }

      // Check if email already exists in list
      const newEmail = toInputValue.trim();
      const emailExists = toEmailList.some((email) => email.value === newEmail);
      const isSelected = toEmails.includes(newEmail);

      // Add to selected emails
      if (!isSelected) {
        setToEmails([...toEmails, newEmail]);
      }

      // Add to email list if doesn't exist
      if (!emailExists) {
        const newEmailOption = {
          label: newEmail,
          value: newEmail,
        };
        setToEmailList([...toEmailList, newEmailOption]);
      }

      // Clear input
      setToInputValue("");
    }
  };
  // Handle Enter key press for CC emails
  const handleCcKeyDown = (e: any) => {
    if (e.key === "Enter" && ccInputValue.trim()) {
      e.preventDefault();

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(ccInputValue)) {
        dispatch(
          showToast({
            severity: "error",
            summary: "Invalid Email",
            detail: "Please enter a valid email address",
            life: 3000,
          })
        );
        return;
      }

      // Check if email already exists in list
      const newEmail = ccInputValue.trim();
      const emailExists = ccEmailList.some((email) => email.value === newEmail);
      const isSelected = ccEmails.includes(newEmail);

      // Add to selected emails
      if (!isSelected) {
        setCcEmails([...ccEmails, newEmail]);
      }

      // Add to email list if doesn't exist
      if (!emailExists) {
        const newEmailOption = {
          label: newEmail,
          value: newEmail,
        };
        setCcEmailList([...ccEmailList, newEmailOption]);
      }

      // Clear input
      setCcInputValue("");
    }
  };

  useEffect(() => {
    if (sendDialogVisible) {
      fetchMailTemplates();
    }
  }, [sendDialogVisible]);

  useEffect(() => {
    fetchCompanyName();
  }, []);

  const fetchCompanyName = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (tenantId) {
      const tenantDetails = await getTenantById(tenantId, dispatch);
      setCompanyName(tenantDetails?.tenancyName || "Default");
    }
  };

  const replaceItemsInTemplate = (html: string) => {
    if (!invoiceData) {
      return html;
    }

    let processedHtml = html;

    // Replace invoice details
    const replacements: Record<string, string> = {
      "{{invoice_number}}": invoiceData
        ? (invoiceData as CreateInvoiceDto)?.invoiceNumber ||
        (invoiceData as GetInvoiceDto)?.invoice_number
        : "",
      "{{customer_name}}":
        (invoiceData as CreateInvoiceDto)?.clientName ||
        ("client_name" in invoiceData ? invoiceData.client_name : ""),
      "{{amount}}": total?.toFixed(2) || "0.00",
      "{{invoice_no}}":
        "invoice_number" in invoiceData ? invoiceData.invoice_number : "",
      "{{invoice_date}}": new Date(invoiceData.issue_date).toLocaleDateString(),
      "{{due_date}}": new Date(invoiceData.due_date).toLocaleDateString(),
      "{{invoice_url}}": `${process.env.REACT_APP_UI_BASE_URL}/invoice/${(invoiceData as any)?.invoice_id
        }`, // Replace with actual URL if available
      "{{company_name}}": `${companyName}`, // Replace with actual company name
      "{{currency_code}}": `${(invoiceData as GetInvoiceDto)?.currency_symbol || "$"
        }`, // Replace with actual company name
    };

    // Apply all text replacements
    Object.entries(replacements).forEach(([placeholder, value]) => {
      processedHtml = processedHtml.replace(
        new RegExp(placeholder, "g"),
        value
      );
    });

    // Process invoice items
    if (invoiceData.invoice_items && invoiceData.invoice_items.length > 0) {
      // Try to find an item row template
      const itemRowMatch = processedHtml.match(
        /<tr[^>]*class="item"[^>]*>([\s\S]*?)<\/tr>/i
      );

      if (itemRowMatch) {
        // We found a template item row, now generate all item rows
        const itemRowTemplate = itemRowMatch[0];
        let allItemRows = "";

        // Generate a row for each invoice item
        invoiceData.invoice_items.forEach((item) => {
          // Create a copy of the template row
          let itemRow = itemRowTemplate;

          // Replace the content with item data
          itemRow = itemRow
            .replace(/{{item_description}}/g, item.description || "")
            .replace(/{{item_quantity}}/g, item.quantity?.toString() || "1")
            .replace(/{{item_price}}/g, item.unit_price?.toFixed(2) || "0.00")
            .replace(/{{tax_rate}}/g, item.sac_gst_rate?.toString() || "0");

          // Legacy support for templates that use Service placeholders
          if (itemRow.includes("Service 1") || itemRow.includes("Service 2")) {
            itemRow = itemRow.replace(/Service \d+/g, item.description || "");

            // Replace price
            const priceMatch = itemRow.match(/\$\d+\.\d+/);
            if (priceMatch) {
              itemRow = itemRow.replace(
                priceMatch[0],
                `${invoiceData.currency_code || "$"}`
              );
            }
          }

          allItemRows += itemRow;
        });

        // Replace all example item rows with our generated ones
        processedHtml = processedHtml.replace(
          /<tr class="item">[\s\S]*?<\/tr>(<tr class="item">[\s\S]*?<\/tr>)*/,
          allItemRows
        );

        // Update the total (legacy support)
        const totalMatch = processedHtml.match(/Total: \$\d+\.\d+/);
        if (totalMatch) {
          processedHtml = processedHtml.replace(
            totalMatch[0],
            `Total: ${invoiceData.currency_code || "$"}${total?.toFixed(2) || "0.00"
            }`
          );
        }
      }
    }

    return processedHtml;
  };

  const fetchMailTemplates = async () => {
    try {
      setLoading(true);
      const response = await getAllMailTemplates(dispatch);
      if (response) {
        setMailTemplates(response);

        // Set default template
        const defaultTemplate = response.find(
          (template) => template.is_default === true
        );
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate);
          const processedTemplate = replaceItemsInTemplate(
            defaultTemplate.template_content
          );
          setEmailMessage(processedTemplate);
        } else if (response.length > 0) {
          // If no default template, select the first one
          setSelectedTemplate(response[0]);
          const processedTemplate = replaceItemsInTemplate(
            response[0].template_content
          );
          setEmailMessage(processedTemplate);
        }
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (template: GetAllMailBodyTemplateDto) => {
    setSelectedTemplate(template);
    const processedTemplate = replaceItemsInTemplate(template.template_content);
    setEmailMessage(processedTemplate);
  };

  const handleMessageEdit = (newMessage: string) => {
    setEmailMessage(newMessage);
  };

  const handleDropdownChange = (e: any, fieldName?: string) => {
    setInvoiceData({
      ...invoiceData,
      choosedTemplateId: e.value,
    });
  };

  const templateOptions = mailTemplates.map((template) => ({
    label: template.template_name,
    value: template,
  }));

  const sendDialogFooter = (
    <div className="flex justify-between items-center w-full">
      {/* Left Side: Checkbox */}
      {
        <div className="flex items-center">
          <Checkbox
            inputId="withPaymentLink"
            checked={withPaymentLink ?? true}
            onChange={(e) => {
              setWithPaymentLink?.(e.checked ?? true);
              if (e.checked) {
                showToastForPaymentSetting();
              }
            }}
          />

          <label htmlFor="withPaymentLink" className="ml-2">
            Send with payment link
          </label>
        </div>
      }

      {/* Right Side: Buttons */}
      <div>
        <Button
          icon="pi pi-times"
          className="p-button-text"
          onClick={() => setSendDialogVisible(false)}
        >
          Cancel
        </Button>
        {/* <Button icon="pi pi-send" onClick={handleSend}>
          Send
        </Button> */}
        {/* Send Button with Overlay */}
        <div
          onMouseEnter={(e) => {
            if(isTenantInfoMissing){
                overlayRef.current?.show(e, e.currentTarget);
            }
              
          }}

          style={{ display: 'inline-block' }}
        >
          <Button
            icon="pi pi-send"
            onClick={handleSend}
          >
            Send
          </Button>
        </div>

        {/* Overlay Panel - keep this always rendered */}
        <OverlayPanel
          ref={overlayRef}
          appendTo={document.body}
          dismissable={false}
          showCloseIcon={false}
          style={{
            zIndex: 9999,
            maxWidth: '300px'
          }}
        >
          <div className="p-3 relative">
            {/* Close button */}
            <button
              onClick={() => { overlayRef.current?.hide(); 
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Close warning"
            >
              ×
            </button>
            <div className="flex align-items-center mb-2">
              <i className="pi pi-exclamation-triangle text-yellow-500 mr-2"></i>
              <span className="font-semibold text-yellow-700">Missing Information</span>
            </div>
            <p className="text-sm text-gray-700 m-0">
              Some tenant details (address, city, etc.) are missing.
              Please update them before sending the invoice.
            </p>
            {/* Navigation link using useNavigate */}
            <button
              onClick={(e:any) => {
                overlayRef.current?.hide();
                
                action && action(e,InvoiceStatusEnum.DRAFT,true)
                navigate('/settings');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer flex items-center bg-transparent border-none p-0"
            >
              <i className="pi pi-cog mr-1"></i>
              Go to Settings to update tenant details
            </button>
          </div>
        </OverlayPanel>
      </div>
    </div>
  );

  const EditablePreview = ({
    html,
    onUpdate,
  }: {
    html: string;
    onUpdate: (newHtml: string) => void;
  }) => {
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (divRef.current) {
        divRef.current.innerHTML = html;

        // Ensure only `.message-section` is editable
        const messageSection = divRef.current.querySelector(".message-section");
        if (messageSection) {
          messageSection.setAttribute("contenteditable", "true");
        }
      }
    }, [html]);

    const handleBlur = () => {
      if (divRef.current) {
        const messageSection = divRef.current.querySelector(".message-section");
        if (messageSection) {
          onUpdate(divRef.current.innerHTML);
        }
      }
    };

    return (
      <div
        ref={divRef}
        className="border border-gray-300 rounded-md p-4 min-h-[200px] bg-white overflow-auto"
        style={{
          maxHeight: "300px",
          fontSize: "14px",
          lineHeight: "1.5",
        }}
        suppressContentEditableWarning
        onBlur={handleBlur}
      />
    );
  };

  const fetchAllEmailIds = () => {
    if (!invoiceData.client_id) return;
    getEmailsByClientId(dispatch, invoiceData.client_id?.toString())
      .then((res) => {
        if (!res) {
          setToEmailList([
            {
              label: emailTo,
              value: emailTo,
            },
          ]);
          return;
        }
        const emails = res.map((val: any) => ({
          label: val,
          value: val,
        }));
        setToEmailList(emails);
        setCcEmailList(emails);
      })
      .catch(() => {
        setToEmailList([
          {
            label: emailTo,
            value: emailTo,
          },
        ]);
      });
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await getAllTemplates(dispatch, {
          Page: 1,
          Limit: 50,
        });

        if (response) {
          const formattedTemplates = response?.List?.map((template: any) => ({
            label: template.template_name,
            value: template.template_id,
            id: template.template_id,
            is_default: template.is_default,
          }));
          setTemplates(formattedTemplates);

          if (!invoiceData.choosedTemplateId && formattedTemplates.length > 0) {
            const defaultTemplate =
              formattedTemplates?.find((t: any) => t.is_default) ||
              formattedTemplates[0];
            setInvoiceData((prev: any) => ({
              ...prev,
              template_id: defaultTemplate.id,
              choosedTemplateId: defaultTemplate.id,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };

    fetchTemplates();
  }, [dispatch]);

  useEffect(() => {
    if (invoiceData.client_id) {
      fetchAllEmailIds();
    }
  }, [invoiceData.client_id]);

  useEffect(() => {
    if (emailTo) {
      const emails = emailTo.split(",").map((email) => email);
      setToEmails(emails);
    }
  }, [emailTo]);

  useEffect(() => {
    if (emailCc) {
      const emails = emailCc.split(",").map((email) => email);
      setCcEmails(emails);
    }
  }, [emailCc]);

  return (
    <Dialog
      header="Send Invoice"
      visible={sendDialogVisible}
      onHide={() => setSendDialogVisible(false)}
      footer={sendDialogFooter}
      style={{ width: "60vw", maxWidth: "800px" }}
    >
      <div className="p-fluid">
        <div className="mb-4">
          <label
            htmlFor="recipients"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Recipients
          </label>
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">To:</span>
            <MultiSelect
              id="recipients"
              optionLabel="label"
              optionValue="value"
              value={toEmails}
              options={toEmailList}
              onChange={(e) => {
                setToEmails(e.value);
              }}
              display="chip"
              placeholder="Select or enter email addresses"
              className="w-full"
              filter
              maxSelectedLabels={3}
              showClear
              onInput={(e: any) => setToInputValue(e.target.value)}
              onKeyDown={handleToKeyDown}
            />
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="cc"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            CC
          </label>
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">CC:</span>
            <MultiSelect
              id="cc"
              optionLabel="label"
              optionValue="value"
              value={ccEmails}
              options={ccEmailList}
              onChange={(e) => {
                setCcEmails(e.value);
              }}
              display="chip"
              placeholder="Select or enter email addresses"
              className="w-full"
              filter
              showClear
              onInput={(e: any) => setCcInputValue(e.target.value)}
              onKeyDown={handleCcKeyDown}
            />
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Subject
          </label>
          <InputTextarea
            id="subject"
            rows={1}
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
          />
        </div>

        {/* invoice template */}
        <div className="flex items-center mb-4 gap-2">
        <div className="mb-4  w-1/2">
          <label
            htmlFor="messageTemplate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Invoice Template
          </label>
          <Dropdown
            id="template_id"
            name="template_id"
            value={invoiceData.choosedTemplateId}
            options={templates}
            onChange={handleDropdownChange}
            optionLabel="label"
            optionValue="value"
            placeholder={localization?.SelectTemplate || "Select Template"}
            className="w-full"
          />
        </div>

        <div className="mb-4 w-1/2">
          <label
            htmlFor="messageTemplate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Template
          </label>
          <Dropdown
            id="messageTemplate"
            value={selectedTemplate}
            options={templateOptions}
            onChange={(e) => handleTemplateChange(e.value)}
            placeholder="Select a message template"
            loading={loading}
            disabled={loading || mailTemplates.length === 0}
            className="w-full"
          />
        </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700"
            >
              Message
            </label>
            <div className="flex gap-2">
              <Button
                type="button"
                className={`p-button-sm ${previewMode ? "p-button-primary" : "p-button-outlined"
                  }`}
                onClick={() => setPreviewMode(true)}
              >
                <i className="pi pi-eye mr-2"></i>
                Preview
              </Button>
            </div>
          </div>

          {previewMode ? (
            <EditablePreview
              html={emailMessage}
              onUpdate={(newHtml) => setEmailMessage(newHtml)}
            />
          ) : (
            <InputTextarea
              id="message"
              rows={10}
              value={emailMessage}
              onChange={(e) => handleMessageEdit(e.target.value)}
              className="font-mono text-sm"
              style={{ resize: "vertical" }}
            />
          )}

          {previewMode && (
            <div className="text-xs text-gray-500 mt-1">
              Switch to HTML mode to edit the message content
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
